import { __awaiter } from 'tslib';
import { currentUser, config, send, script, decode } from '@onflow/fcl';
import { DynamicError } from '../../errors.js';
import { logger } from '../../utils/logger.js';

class FlowProvider {
    constructor({ appName, appLogoUrl, }) {
        this.appName = appName;
        this.appLogoUrl = appLogoUrl;
    }
    isInstalledOnBrowser() {
        return false;
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    teardownEventListeners() {
        return;
    }
    connect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.setupConfig();
            const { addr } = yield currentUser().authenticate();
            if (addr) {
                yield ((_a = this.onAccountChange) === null || _a === void 0 ? void 0 : _a.call(this, [addr]));
            }
        });
    }
    setupEventListeners(listeners) {
        this.onAccountChange = listeners.onAccountChange;
    }
    setupConfig() {
        this.baseConfig();
    }
    baseConfig() {
        config()
            .put('app.detail.title', this.appName)
            .put('app.detail.icon', this.appLogoUrl)
            .put('accessNode.api', 'https://rest-mainnet.onflow.org')
            .put('flow.network', 'mainnet');
    }
    getDeepLink() {
        return undefined;
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.connect();
                const user = yield currentUser().snapshot();
                return user.addr;
            }
            catch (error) {
                logger.error(error);
                return undefined;
            }
        });
    }
    proveOwnership(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.connect();
                yield this.signDelay();
                const signedMessage = yield this.signMessage(messageToSign);
                if (!signedMessage) {
                    return undefined;
                }
                const data = {
                    appName: this.appName,
                    signedMessage: JSON.parse(signedMessage),
                };
                return JSON.stringify(data);
            }
            catch (error) {
                logger.error(error);
                return undefined;
            }
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const message = Buffer.from(messageToSign).toString('hex');
            const signedMessage = yield currentUser().signUserMessage(message);
            if (signedMessage.includes('Declined')) {
                throw new DynamicError('User declined the signature');
            }
            return JSON.stringify(signedMessage);
        });
    }
    getWeb3Provider() {
        return undefined;
    }
    getRpcProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            return;
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            this.baseConfig();
            const address = yield this.fetchPublicAddress();
            if (address) {
                const code = `
      pub fun main(): UFix64 {
        let account = getAccount(${address});
        return account.balance;
      }
      `;
                try {
                    return yield send([script(code)]).then(decode);
                }
                catch (error) {
                    logger.error(error);
                    return undefined;
                }
            }
            else {
                return undefined;
            }
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            currentUser().unauthenticate();
        });
    }
    supportsNetworkSwitching() {
        return false;
    }
    switchNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new DynamicError('Not supported');
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield currentUser().snapshot();
            if (user.addr) {
                return [user.addr];
            }
            return [];
        });
    }
    signDelay() {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve();
        });
    }
}

export { FlowProvider };
